// src/subscreens/mechanicadmin/dashboard/MechanicDashboard.jsx

import React, { useState, useContext, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import {
  FiTool,
  FiCheckCircle,
  FiClipboard,
  FiArrowRight,
  FiCalendar,
  FiUser,
  FiX,
  FiRefreshCw,
} from "react-icons/fi";
import { FaWrench } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import MechanicAdminLayout from "../../../layout/mechanic/MechanicMasterLayout.js";
import "../../../assets/css/mechanic/mechanicdashboard/MechanicDashboard.css";
import AppContext from "../../../context/AppContext.js";
import axios from "axios";

/* ─── KYC Benefits ─── */
const kycBenefits = [
  "Accept maintenance requests from farmers",
  "Receive payments directly to your account",
  "Get verified mechanic badge on your profile",
  "Build trust with farmers across Pakistan",
];

const MechanicDashboard = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken, isMechanicVerified } = useContext(AppContext);

  const [showKycModal, setShowKycModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [dashboardStats, setDashboardStats] = useState({
    totalRequests: 0,
    activeJobs: 0,
    completedJobs: 0,
    topupRequests: 0,
  });

  const [mechanicCommissionRate, setMechanicCommissionRate] = useState(0);
  const [minimumTopupAmount, setMinimumTopupAmount] = useState(1000);

  const [activeJobsList, setActiveJobsList] = useState([]);
  const [topupRequestsList, setTopupRequestsList] = useState([]);

  const [activeCard, setActiveCard] = useState(null);

  // ── Fetch Dashboard Stats (dynamic) ──
  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/mechanic-admin/dashboard`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          setDashboardStats(response.data.stats);
          setActiveJobsList(response.data.activeJobsList || []);
          setTopupRequestsList(response.data.topupRequestsList || []);
          setMechanicCommissionRate(response.data.mechanicCommissionRate || 0);
          setMinimumTopupAmount(response.data.minimumTopupAmount || 1000);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getDashboardStats();
  }, []);

  useEffect(() => {
    if (!isMechanicVerified) {
      setShowKycModal(true);
    }
  }, [isMechanicVerified]);

  // ── Stat Cards Data (dynamic values sy) ──
  const statCards = [
    {
      id: 1,
      icon: <FiClipboard />,
      count: dashboardStats.totalRequests,
      label: "Total Requests",
    },
    {
      id: 2,
      icon: <FiTool />,
      count: dashboardStats.activeJobs,
      label: "Active Jobs",
    },
    {
      id: 3,
      icon: <FiCheckCircle />,
      count: dashboardStats.completedJobs,
      label: "Completed Jobs",
    },
    {
      id: 4,
      icon: <FiRefreshCw />,
      count: dashboardStats.topupRequests,
      label: "Top-up Requests",
    },
  ];

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <MechanicAdminLayout>
      <div className="mdb-page">
        {/* ── Welcome Banner ── */}
        <div className="mdb-welcome-banner">
          <div className="mdb-welcome-text">
            <h2 className="mdb-welcome-title">
              Welcome Back, Mechanic! <span>🔧</span>
            </h2>
            <p className="mdb-welcome-sub">
              You have{" "}
              <strong>
                {dashboardStats.totalRequests} maintenance requests
              </strong>{" "}
              waiting for your response.
            </p>
          </div>
          <div className="mdb-welcome-icon">
            <FaWrench />
          </div>
        </div>

        {/* ── Commission + Minimum Topup Info Strip (banner k neeche, dynamic) ── */}
        <div className="mdb-commission-strip">
          <span className="mdb-commission-text">
            You pay{" "}
            <strong>
              {isLoading
                ? "..."
                : `Rs ${Number(mechanicCommissionRate).toLocaleString()}`}
            </strong>{" "}
            commission to Smart Agri Rent on every completed job.
          </span>
          <span className="mdb-commission-divider">•</span>
          <span className="mdb-commission-text">
            Minimum top-up amount:{" "}
            <strong>Rs {Number(minimumTopupAmount).toLocaleString()}</strong>
          </span>
        </div>

        {/* ── Stat Cards ── */}
        <div className="mdb-stats-container">
          <Row className="g-4">
            {statCards.map((card) => (
              <Col key={card.id} xl={3} lg={3} md={6} sm={12}>
                <div
                  className={`mdb-stat-card ${activeCard === card.id ? "mdb-stat-card-active" : ""}`}
                  style={{ borderLeft: "4px solid #FDB714" }}
                  onClick={() =>
                    setActiveCard(activeCard === card.id ? null : card.id)
                  }
                >
                  <div className="mdb-stat-icon">{card.icon}</div>
                  <div className="mdb-stat-content">
                    <h3>{isLoading ? "..." : card.count}</h3>
                    <p>{card.label}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* ── Active Jobs + Top-up Requests ── */}
        <Row className="g-3 mb-4">
          {/* Active Jobs */}
          <Col lg={7} md={12}>
            <div className="mdb-card">
              <div className="mdb-card-header">
                <div className="mdb-card-header-left">
                  <span className="mdb-card-icon">
                    <FiTool />
                  </span>
                  <h3 className="mdb-card-title">Active Jobs</h3>
                </div>
                <button
                  className="mdb-view-all-btn"
                  onClick={() => navigate("/mechanic-admin/requests")}
                >
                  View All <FiArrowRight />
                </button>
              </div>
              <div className="mdb-card-body">
                {isLoading ? (
                  <p className="mdb-empty-text">Loading...</p>
                ) : activeJobsList.length === 0 ? (
                  <p className="mdb-empty-text">No active jobs right now.</p>
                ) : (
                  activeJobsList.map((job, i) => (
                    <div className="mdb-active-job-item" key={i}>
                      <div className="mdb-job-top">
                        <div className="mdb-job-info">
                          <span className="mdb-job-id">MR-{job.id}</span>
                          <span className="mdb-job-equip">
                            {job.machinerytype}
                          </span>
                        </div>
                        <span className="mdb-badge mdb-badge-inprogress">
                          In Progress
                        </span>
                      </div>
                      <div className="mdb-job-meta">
                        <span>
                          <FiUser /> {job.fullname}
                        </span>
                        <span>
                          <FiCalendar /> {formatDate(job.bookingdate)}
                        </span>
                      </div>
                      <p className="mdb-review-comment">
                        {job.descriptionissue}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>

          {/* Top-up Requests */}
          <Col lg={5} md={12}>
            <div className="mdb-card">
              <div className="mdb-card-header">
                <div className="mdb-card-header-left">
                  <span className="mdb-card-icon">
                    <FiRefreshCw />
                  </span>
                  <h3 className="mdb-card-title">Top-up Requests</h3>
                </div>
                <button
                  className="mdb-view-all-btn"
                  onClick={() => navigate("/mechanic-admin/earnings")}
                >
                  View All <FiArrowRight />
                </button>
              </div>
              <div className="mdb-card-body">
                {isLoading ? (
                  <p className="mdb-empty-text">Loading...</p>
                ) : topupRequestsList.length === 0 ? (
                  <p className="mdb-empty-text">No top-up requests.</p>
                ) : (
                  topupRequestsList.map((topup, i) => (
                    <div className="mdb-review-item" key={i}>
                      <div className="mdb-review-top">
                        <div className="mdb-review-meta">
                          <span className="mdb-review-farmer">
                            Rs {Number(topup.amount || 0).toLocaleString()}
                          </span>
                          <span className="mdb-review-job">
                            Status: {topup.status}
                          </span>
                        </div>
                        <span className="mdb-review-date">
                          {formatDate(topup.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ════════════════════════════════════════
          KYC VERIFICATION MODAL
      ════════════════════════════════════════ */}
      {showKycModal && (
        <div className="kyc-overlay" onClick={() => setShowKycModal(false)}>
          <div className="kyc-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="kyc-close-btn"
              onClick={() => setShowKycModal(false)}
            >
              <FiX size={16} />
            </button>

            <div className="kyc-top">
              <div className="kyc-icon-wrap">
                <FiCheckCircle size={34} color="#fff" />
              </div>

              <h2 className="kyc-title">Complete Your Verification</h2>

              <p className="kyc-subtitle">
                To start working on{" "}
                <strong className="kyc-brand">AgriRent</strong>, please complete
                your KYC verification. This helps us ensure a safe and trusted
                marketplace for all users.
              </p>

              <div className="kyc-benefits-box">
                <p className="kyc-benefits-heading">
                  Benefits of Verification:
                </p>
                {kycBenefits.map((benefit, i) => (
                  <div className="kyc-benefit-item" key={i}>
                    <div className="kyc-benefit-check">
                      <FiCheckCircle size={14} />
                    </div>
                    <span className="kyc-benefit-text">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="kyc-bottom">
              <button
                className="kyc-verify-btn"
                onClick={() => {
                  setShowKycModal(false);
                  navigate("/mechanic-admin/kyc");
                }}
              >
                Verify Now
              </button>
              <p className="kyc-note">
                You can complete verification anytime from Settings → Documents
              </p>
            </div>
          </div>
        </div>
      )}
    </MechanicAdminLayout>
  );
};

export default MechanicDashboard;